import Cookies from "js-cookie"
import { sortBy, toPairs } from "lodash-es"
import { getUtmParams } from "~/helpers/utmParams"
import {
  getGoogleAnalyticsSessionData,
  getCampAnonymousId
} from "~/providers/AnalyticsProvider"
import { DateTime } from "luxon"
import { type SerializeFrom } from "@remix-run/node"
import type { PublicConfigValues } from "~/providers/ConfigProvider"
import type { WithShopifyCustomerFragment } from "~/shopifyCustomerAccounts"
import { ClientError } from "graphql-request"

export const CHECKOUT_KEY = "camp:cartId"

export const toShopifyStorefrontId = (type: string, id: string) =>
  `gid://shopify/${type}/${id}`

export const fromShopifyStorefrontId = (id: string) =>
  id.replace(/\?.*/, "").split("/").pop() as string

export const shopifyImageUrl = (
  url: string,
  options: { width: number; height: number }
) => url.replace(/(\.\w+\?)/, `_${options.width}x${options.height}$1`)

export const createPersistedCheckout = async (
  googleAnalyticsMeasurementId: string,
  shopify: any
) => {
  let checkout = await findPersistedCheckout(shopify)

  const customAttributes = await getCustomAttributes(
    googleAnalyticsMeasurementId
  )

  if (!checkout) {
    checkout = await shopify.checkout.create({ customAttributes })
    localStorage.setItem(CHECKOUT_KEY, String(checkout.id))
  }

  return checkout
}

export const findPersistedCheckout = async (shopify: any) => {
  const checkoutId = localStorage.getItem(CHECKOUT_KEY)

  const checkout = checkoutId ? await shopify.checkout.fetch(checkoutId) : null

  if (checkout && checkout.completedAt) {
    return null
  }

  return checkout
}

export const simplifyShopifyObject = (object: any) =>
  JSON.parse(JSON.stringify(object))

export const shopifyVariantIdToNumber = (id: string) => {
  const parsedId = fromShopifyStorefrontId(id) || ""
  return Number(parsedId.replace(/\?.+/, ""))
}

type CartActivityProperties = SerializeFrom<{
  id: string
  startsAt: any
  startsAtTime: string
  activityType: {
    id: string
    name: string
    partner?: string | null | undefined
    experience?: boolean | null | undefined
  }
  adultsEnabled: boolean
  location: {
    id: string
    name: string
    netsuiteId?: string | null
    isVirtual: boolean
    timezone: string
  }
}>

export const activityCartProperties = ({
  activity,
  adults,
  children,
  createdAt
}: {
  activity: CartActivityProperties
  adults?: number
  children: number
  createdAt?: string
}): Record<string, string> => {
  return {
    activity_id: activity.id,
    activity_type_id: activity.activityType.id,
    activity_type_name: activity.activityType.name,
    activity_starts_at_formatted: DateTime.fromISO(activity.startsAt)
      .setZone(activity.location.timezone)
      .toFormat("MMM d, yyyy h:mma"),
    activity_time: activity.startsAtTime,
    activity_venue: activity.location.name,
    activity_virtual: String(activity.location.isVirtual),
    ...(activity.adultsEnabled ? { adults: String(adults) } : {}),
    children: String(children),
    location_netsuite_id: activity.location.netsuiteId ?? "",
    date: DateTime.fromISO(activity.startsAt)
      .setZone(activity.location.timezone)
      .toFormat("MM/dd/yyyy"),
    created_at: createdAt ?? "",
    is_experience: activity.activityType.experience ? "true" : "false"
  }
}

export const activityUpsellCartProperties = ({
  activity
}: {
  activity: CartActivityProperties
}): Record<string, string> => {
  return {
    activity_id: activity.id,
    activity_type_id: activity.activityType.id,
    activity_type_name: activity.activityType.name,
    activity_starts_at_formatted: DateTime.fromISO(activity.startsAt)
      .setZone(activity.location.timezone)
      .toFormat("MMM d, yyyy h:mma"),
    activity_time: activity.startsAtTime,
    activity_venue: activity.location.name,
    activity_virtual: String(activity.location.isVirtual),
    location_netsuite_id: activity.location.netsuiteId ?? "",
    date: DateTime.fromISO(activity.startsAt)
      .setZone(activity.location.timezone)
      .toFormat("MM/dd/yyyy")
  }
}

export const objectToAttributes = (object: Record<string, string>) =>
  toPairs(object).map(([key, value]) => ({
    key,
    value
  }))

const SIZE_NAMES = [
  "S",
  "Small",
  "M",
  "Medium",
  "L",
  "Large",
  "XL",
  "X-Large",
  "XXL",
  "XX-Large"
]

export const sortSizes = (sizes: string[]) => {
  let mixed: string[] = []
  let integers: string[] = []
  let strings: string[] = []

  sizes.forEach(size => {
    const i = parseInt(size)
    if (i) {
      if (size.match(/[A-Za-z]/)) {
        mixed.push(size)
      } else {
        integers.push(size)
      }
    } else {
      strings.push(size)
    }
  })

  mixed = sortBy(mixed, parseInt)
  integers = sortBy(integers, parseInt)
  strings = sortBy(strings, i => SIZE_NAMES.indexOf(i))
  return [...mixed, ...integers, ...strings]
}

interface OptionConfig {
  sortFn?: (options: string[]) => string[]
  formatFn?: (options: string) => string
  alernativeStyle?: "buttons"
}

export const OPTION_CONFIG: Record<string, OptionConfig | undefined> = {
  size: {
    sortFn: sortSizes
  },
  Value: {
    formatFn: (option: string) => `$${option}`,
    sortFn: (options: string[]) => sortBy(options, parseFloat)
  }
}

/**
 * Returns an array of custom attributes for a Shopify Order.
 *
 */
export const getCustomAttributes = async (
  googleAnalyticsMeasurementId: string
) => {
  const customAttributes = []
  // Add GA4 attributes.
  const sessionData = await getGoogleAnalyticsSessionData(
    googleAnalyticsMeasurementId
  )

  if (sessionData) {
    customAttributes.push({
      key: "google_client_id",
      value: sessionData.clientId
    })
    customAttributes.push({
      key: "google_analytics_session_id",
      value: sessionData.sessionId
    })
    customAttributes.push({
      key: "google_analytics_session_number",
      value: String(sessionData.sessionNumber)
    })
  }

  // Add UTM params.
  const utmParams: { [key: string]: string } = getUtmParams()

  Object.keys(utmParams).forEach(key => {
    customAttributes.push({
      key,
      value: utmParams[key]
    })
  })

  // Get Facebook attributes.
  const fbpValue = Cookies.get("_fbp")
  if (fbpValue) {
    customAttributes.push({
      key: "fbp",
      value: fbpValue
    })
  }

  const fbcValue = Cookies.get("_fbc")
  if (fbcValue) {
    customAttributes.push({
      key: "fbc",
      value: fbcValue
    })
  }

  const opprefValue = Cookies.get("__oppref")
  if (opprefValue) {
    customAttributes.push({
      key: "oppref",
      value: opprefValue
    })
  }

  const caId = getCampAnonymousId()
  if (caId) {
    customAttributes.push({
      key: "ca_id",
      value: caId
    })
  }

  return customAttributes
}

export const isTopBunkMember = (customer: WithShopifyCustomerFragment) => {
  return (
    customer.tags?.includes("member") ||
    customer.tags?.includes("top bunk member")
  )
}

export const getMembershipVersion = (customer: WithShopifyCustomerFragment) => {
  return customer?.membershipVersion?.value
}

export const maskErrors = (e: unknown) => {
  if (e instanceof ClientError) {
    throw new Error(
      e.response.errors?.at(0)?.message ?? "Unknown backend API error"
    )
  }

  throw e
}

/**
 * Returns an array of redirect attributes for a Shopify Order.
 *
 */
export const getRedirectAttributes = (publicConfig: PublicConfigValues) => {
  const customAttributes: { value: string; key: string }[] = []

  if (publicConfig.webhookRedirect && publicConfig.webhookRedirectUrl) {
    customAttributes.push({
      key: "webhook_redirect_url",
      value: publicConfig.webhookRedirectUrl
    })
  }

  if (publicConfig.myShowPageRedirect) {
    customAttributes.push({
      key: "my_show_page_base",
      value: publicConfig.frontendBase
    })
  }

  return customAttributes
}
